import React, { useEffect, useRef, useState, memo } from 'react';
import { widget } from '../charting_library'; // Import the TradingView charting library
import './TradingViewChart.css';
import CustomDatafeed from './CustomDatafeed_v2'
import { fetchAndStoreMarketData } from './fetchAndStoreMarketData_v2'; // Import fetchAndStoreMarketData
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import IconButton from '@mui/material/IconButton';
import { Grid, Box, Toolbar, TextField, Snackbar, Alert, CircularProgress } from '@mui/material';
import MDTypography from "components/MDTypography";
import { useTheme } from '@mui/material/styles';
import Draggable from 'react-draggable'; // Import Draggable
import Tooltip from '@mui/material/Tooltip';
import RefreshIcon from '@mui/icons-material/Refresh';
import { checkTakeProfitStopLoss, checkLimitOrderHit, updateTrades, calculateUnrealizedProfit, applyTrailingStopLoss, getContractSize } from './FXBacktesting_v2'; // Import the functions
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';


function getLanguageFromURL() {
  const regex = new RegExp('[\\?&]lang=([^&#]*)');
  const results = regex.exec(window.location.search);
  return results === null ? null : decodeURIComponent(results[1].replace(/\+/g, ' '));
}

export const TradingViewChart = memo(({ 
  symbol, 
  startDate, 
  endDate, 
  currentSessionId, 
  sessionId, 
  userID, 
  startDateMillis, 
  endDateMillis, 
  interval = '60', 
  apiInterval,
  onDatafeedReady,
  trades = [], // Pass trades as a prop
  setTrades,
  updateTrades, // Function to update trades
  handleIsOrderModalOpen,  // Accept the placeOrder function as a prop
  handleDrawingToolClick, 
  onDrawingToolClick,
  onSaveLayout, // Function to save layout to parent
  onLoadLayout, 
  applyTrailingStopLoss, // Function to load saved layout
  contractSize,
  isDemo,
  setTradeSnackbarMessage, setTradeSnackbarColor, setTradeSnackbarOpen,
}) => {

  const chartContainerRef = useRef(null);
  const chartWidgetRef = useRef(null); // Use a ref to store the chart widget instance
  const [isLoading, setIsLoading] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const intervalRef = useRef(null);
  const [currentResolution, setCurrentResolution] = useState(null);
  const theme = useTheme();
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const iconColor = theme.palette.mode === 'dark' ? theme.palette.grey[200] : theme.palette.grey[600];
  const [isChartReady, setIsChartReady] = useState(false);
  const candleSeries = useRef([]);
  const [selectedInterval, setSelectedInterval] = useState(interval);
  const [chartTheme, setChartTheme] = useState('Light');
  const [drawingToolValues, setDrawingToolValues] = useState(null);
  const [lastClickedDrawingId, setLastClickedDrawingId] = useState(null); 
  const [showEndSessionSnackbar, setShowEndSessionSnackbar] = useState(false);
  const [showDemoAlert, setShowDemoAlert] = useState(true);
  const [showFeatureAlert, setShowFeatureAlert] = useState(false);
  const [isIntervalLoading, setIsIntervalLoading] = useState(false);
  
  
  // Add demo notification component:
  const DemoRestrictionAlert = () => (
    <>
      <Snackbar 
        open={showDemoAlert} 
        autoHideDuration={6000} 
        onClose={() => setShowDemoAlert(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ marginTop: '20px' }}
      >
        <Alert severity="info" onClose={() => setShowDemoAlert(false)}>
          Demo Account Restrictions: Limited to 1 session, 30-day timeframe. 
          Features disabled: Order placement, interval switching.
        </Alert>
      </Snackbar>
      
      <Snackbar
        open={showFeatureAlert}
        autoHideDuration={3000}
        onClose={() => setShowFeatureAlert(false)}
      >
        <Alert severity="warning" onClose={() => setShowFeatureAlert(false)}>
          This feature is restricted in demo mode
        </Alert>
      </Snackbar>
    </>
  );
  
  const toggleChartTheme = () => {
    setChartTheme((prevTheme) => {
      const newTheme = prevTheme === 'Light' ? 'Dark' : 'Light';
  
      if (chartWidgetRef.current) {
        console.log('Changing theme to:', newTheme);
        chartWidgetRef.current.changeTheme(newTheme === 'Light' ? 'Light' : 'Dark');
        chartWidgetRef.current.applyOverrides({
          'mainSeriesProperties.candleStyle.upColor': 'orange',
          'mainSeriesProperties.candleStyle.downColor': 'black',
          'mainSeriesProperties.candleStyle.borderUpColor': 'black',
          'mainSeriesProperties.candleStyle.borderDownColor': 'black',
          'mainSeriesProperties.candleStyle.wickUpColor': 'black',
          'mainSeriesProperties.candleStyle.wickDownColor': 'black',
          'paneProperties.background': newTheme === 'Light' ? '#ffffff' : '#131722',
          'scalesProperties.textColor': newTheme === 'Light' ? '#000000' : '#ffffff',
          'paneProperties.vertGridProperties.color': newTheme === 'Light' ? '#e1e1e1' : '#363c4e',
          'paneProperties.horzGridProperties.color': newTheme === 'Light' ? '#e1e1e1' : '#363c4e',
          // Add toolbar-specific overrides
          'editorCharts.backgroundColor': newTheme === 'Light' ? '#ffffff' : '#131722',
          'toolbar_bg': newTheme === 'Light' ? '#f1f3f6' : '#1c2526', // Matches TradingView’s dark toolbar
        });
  
        // Force CSS update for the drawing toolbar if needed
        const toolbar = chartContainerRef.current.querySelector('.drawingToolbar-2z9nTEQs');
        if (toolbar) {
          toolbar.style.backgroundColor = newTheme === 'Light' ? '#ffffff' : '#1c2526';
        }
      }
  
      return newTheme;
    });
  };
  
  const LoadingOverlay = () => (
    <Box
      sx={{
        position: 'absolute', // Changed to absolute to stay within chart container
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.7)', // Darker semi-transparent background
        zIndex: 1000, // Ensure it’s above the chart but below snackbars
      }}
    >
      <Box
        sx={{
          backgroundColor: theme.palette.background.paper, // Use paper for better contrast
          borderRadius: '12px',
          p: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          boxShadow: '0px 8px 30px rgba(0, 0, 0, 0.5)', // Stronger shadow
        }}
      >
        <CircularProgress size={60} sx={{ mb: 2, color: theme.palette.primary.main }} />
        <MDTypography variant="h6" color="textPrimary">
          Loading new interval data...
        </MDTypography>
      </Box>
    </Box>
  );

  useEffect(() => {
    const renderChartWithDelay = async () => {
      setTimeout(async () => {
        if (chartContainerRef.current && !chartWidgetRef.current) {
          console.log('Rendering chart for symbol:', symbol);
          // Log parameters
          console.log('Parameters:', {
            symbol,
            interval,
            startDate,
            endDate,
            sessionId,
            userID,
            startDateMillis,
            endDateMillis
          });

          // Initialize the TradingView chart widget
          console.log('Initializing chart widget...');
          const datafeed = new CustomDatafeed(symbol, interval, selectedInterval, startDate, endDate, sessionId, userID, startDateMillis, endDateMillis, chartWidgetRef.current);
          // Ensure the datafeed instance is correctly initialized
          if (!datafeed) {
            console.error("Failed to create datafeed.");
            return;
          }         
          console.log('Datafeed instance:', datafeed);
          if (onDatafeedReady) {
            onDatafeedReady(datafeed); // Pass the datafeed instance back to FXBacktesting.js
          }
          chartWidgetRef.current = new widget({
            symbol: symbol, 
            datafeed: datafeed,
            container: chartContainerRef.current,
            library_path: '/charting_library/',
            locale: getLanguageFromURL() || 'en',
            toolbar_bg: chartTheme === 'Light' ? '#f1f3f6' : '#141414', // Set initial value
            overrides: {
              // ... existing overrides
              'toolbar_bg': chartTheme === 'Light' ? '#f1f3f6' : '#141414',
            },
            interval: interval, 
            disabled_features: [
              'use_localstorage_for_settings',
              ...(isDemo ? [
                'timeframes_toolbar',
                'create_orders',
                'order_panel',
                'header_interval_dialog_button',
                'show_interval_dialog_on_key_press',
                'header_interval_dialog_button',
                'header_symbol_search',
                'header_resolutions', // Add this
                'time_frames_dropdown', // Add this
              ] : []),
            ],
            enabled_features: [
              'study_templates',
              'dome_widget',
              'widget_logo',
              'header_widget',
              ...(isDemo ? [] : ['timeframes_toolbar']), // Move to conditional
              'indicators',
              'fullscreen_button',
              'drawing',
              'drawing_toolbar',
              'control_bar',
              'chart_crosshair_menu',
              'left_toolbar',
              'disable_resolution_rebuild',
              `side_toolbar_in_fullscreen_mode`,
              `header_in_fullscreen_mode`,
              ...(isDemo ? [] : ['order_panel']),
            ],
            charts_storage_url: 'https://saveload.tradingview.com',
            client_id: 'tradingview.com',
            user_id: 'public_user_id',
            autosize: true,
            disableScrolling: true,
            theme: chartTheme,
            debug: false,
            overrides: {
              'mainSeriesProperties.candleStyle.upColor': 'orange',
              'mainSeriesProperties.candleStyle.downColor': 'black',
              'mainSeriesProperties.candleStyle.borderUpColor': 'black',
              'mainSeriesProperties.candleStyle.borderDownColor': 'black',
              'mainSeriesProperties.candleStyle.wickUpColor': 'black',
              'mainSeriesProperties.candleStyle.wickDownColor': 'black',
              'paneProperties.background': chartTheme === 'Light' ? '#ffffff' : '#131722',
              'scalesProperties.textColor': chartTheme === 'Light' ? '#000000' : '#ffffff',
              'paneProperties.vertGridProperties.color': chartTheme === 'Light' ? '#e1e1e1' : '#363c4e',
              'paneProperties.horzGridProperties.color': chartTheme === 'Light' ? '#e1e1e1' : '#363c4e',
              'chartProperties.background': chartTheme === 'Light' ? '#ffffff' : '#131722',
              'toolbarProperties.backgroundColor': chartTheme === 'Light' ? '#ffffff' : '#131722'  
              
            },
            drawings_access: {
              type: 'white',
              tools: [
                { name: 'Trend Line' },
                { name: 'Long Position' },
                { name: 'Short Position' },
                { name: 'Horizontal Line' },
                { name: 'Vertical Line' },
                { name: 'Arrow' },
                { name: 'Ray' },
                { name: 'Extended Line' },
                { name: 'Parallel Channel' },
                { name: 'Regression Trend' },
                { name: 'Fib Retracement' },
                { name: 'Fib Time Zone' },
                { name: 'Fib Fan' },
                { name: 'Fib Arc' },
                { name: 'Fib Extension' },
                { name: 'Fib Channel' },
                { name: 'Gann Fan' },
                { name: 'Gann Square' },
                { name: 'Gann Box' },
                { name: 'Cycle Lines' },
                { name: 'Text' },
                { name: 'Anchored Text' },
                { name: 'Note' },
                { name: 'Price Label' },
                { name: 'Arrow Marker' },
                { name: 'Balloon' },
                { name: 'Image' },
                { name: 'Measure' },
                { name: 'Date Range' },
                { name: 'Price Range' },
                { name: 'Date and Price Range' },
                { name: 'Projection' },
                { name: 'Cross Line' },
                { name: 'Brush' },
                { name: 'Rectangle' },
                { name: 'Ellipse' },
                { name: 'Triangle' },
                { name: 'Polygon' },
                { name: 'Curve' },
                { name: 'Path' },
                { name: 'Icon' },
                { name: 'Pattern' },
                { name: 'Head and Shoulders' },
                { name: 'Triangle Pattern' },
                { name: 'Double Top/Bottom' },
                { name: 'Triple Top/Bottom' },
                { name: 'Flag' },
                { name: 'Pennant' },
                { name: 'Channel' },
                { name: 'Gartley Pattern' },
                { name: 'Wedge' },
                { name: 'Diamond' },
                { name: 'Rounding Bottom' },
                { name: 'Cup and Handle' },
                { name: 'Complex Head and Shoulders' },
              ],
            },
          });
          console.log('Chart widget initialized:', chartWidgetRef.current);
          console.log('Datafeed initialized:', chartWidgetRef.current._options.datafeed);
          setIsLoading(false); // Update loading state

          // Add custom button to the TradingView toolbar
          chartWidgetRef.current.onChartReady(() => {
            setIsChartReady(true);
            setIsLoading(false);
            console.log('Chart is ready');
          
            // Wait for the header to be ready before creating buttons
            chartWidgetRef.current.headerReady().then(() => {
              // Create the button directly
              const button = chartWidgetRef.current.createButton();
              
              // Set button properties
              button.title = 'Toggle Theme';
              button.className = 'apply-common-tooltip';
              button.style.cursor = 'pointer';
              
              // Set initial icon based on current theme
              button.innerHTML = chartTheme === 'Light' ? '🌞 Change Theme' : '🌜 Change Theme'; // Updated icon and text
            
              // Add click event listener
              button.addEventListener('click', () => {
                console.log('Button clicked'); // Check if click is triggered
            
                // Toggle theme and update button icon
                const newTheme = chartTheme === 'Light' ? 'Dark' : 'Light';
          
                toggleChartTheme();
                
                // Update icon based on the new theme
                button.innerHTML = newTheme === 'Light' ? '🌞 Change Theme' : '🌜 Change Theme'; // Updated icon and text
                console.log('Theme set to:', newTheme);
              });
            
              console.log('Button created and added:', button);
              // Add save layout button
              // Comment out save layout button code
              /*
              const saveLayoutButton = chartWidgetRef.current.createButton();
              saveLayoutButton.title = 'Save Layout';
              saveLayoutButton.className = 'apply-common-tooltip';
              saveLayoutButton.style.cursor = 'pointer';
              saveLayoutButton.innerHTML = '💾 Save Layout';
              saveLayoutButton.addEventListener('click', async () => {
                // ... rest of save handler code
              });

                try {
                  if (!chartWidgetRef.current) return;
              
                  const chart = chartWidgetRef.current;
                  const activeChart = chart.activeChart();
                  const shapes = activeChart.getAllShapes();
                  const allDrawings = [];
              
                  for (const shape of shapes) {
                    try {
                      const drawingData = await activeChart.getShapeById(shape.id);
                      console.log('Drawing data source:', drawingData._source);
              
                      // Get stop loss and take profit values for position markers
                      let stopPrice, profitPrice;
                      if (shape.name.includes('position')) {
                        const stopLevel = drawingData._source._properties.stopLevel?._value;
                        const profitLevel = drawingData._source._properties.profitLevel?._value;
                        const entryPrice = drawingData._source._points[0].price;
                        const pipMultiplier = 0.00001;
              
                        if (stopLevel) {
                          const stopOffset = stopLevel * pipMultiplier;
                          stopPrice = shape.name === 'long_position' 
                            ? entryPrice - stopOffset 
                            : entryPrice + stopOffset;
                        }
              
                        if (profitLevel) {
                          const profitOffset = profitLevel * pipMultiplier;
                          profitPrice = shape.name === 'long_position'
                            ? entryPrice + profitOffset
                            : entryPrice - profitOffset;
                        }
                      }
              
                      const shapeData = {
                        id: shape.id,
                        name: shape.name,
                        shape: shape.name,
                        overrides: Object.assign({}, shape.overrides) || {},
                        text: shape.text || '',
                        lock: Boolean(shape.lock),
                        properties: {
                          stopPrice: stopPrice,
                          profitPrice: profitPrice,
                          currentTime: drawingData._source._points[0].time,
                          coordinates: drawingData._source._points.map(point => ({
                            price: Number(point.price),
                            time: Number(point.time)
                          }))
                        }
                      };
              
                      // Get points from _source._points
                      const sourcePoints = drawingData._source._points || [];
                      
                      if (sourcePoints.length > 0) {
                        // Convert points to plain objects
                        shapeData.points = sourcePoints.map(point => ({
                          price: Number(point.price) || 0,
                          time: Number(point.time) || 0
                        }));
                        
                        // Remove any undefined/null values
                        shapeData.points = shapeData.points.filter(point => 
                          point.price !== undefined && 
                          point.time !== undefined
                        );
              
                        if (shapeData.points.length > 0) {
                          allDrawings.push(shapeData);
                          console.log('Saved points for shape:', shape.id, shapeData.points);
                        }
                      } else {
                        console.warn('No points found for shape:', shape.id);
                      }
              
                    } catch (err) {
                      console.error('Error processing shape:', shape.id, err);
                    }
                  }
              
                  // Convert to plain object
                  const layoutData = {
                    drawings: allDrawings.map(drawing => ({...drawing})),
                    settings: {
                      theme: chartTheme || 'Light',
                      timeframe: selectedInterval || '60'
                    }
                  };
              
                  // Final serialization
                  const cleanLayoutData = JSON.parse(JSON.stringify(layoutData));
                  console.log('Saving layout:', cleanLayoutData);
                  await onSaveLayout(cleanLayoutData);
              
                } catch (err) {
                  console.error('Error saving layout:', err);
                }
              });
              
// Load layout handler
if (onLoadLayout) {
  setTimeout(() => {
    onLoadLayout().then(layoutData => {
      if (layoutData?.drawings?.length) {
        const activeChart = chartWidgetRef.current.activeChart();

        layoutData.drawings.forEach(drawing => {
          try {
            console.log('Loading drawing with data:', drawing);

            if (drawing.shape.includes('position')) {
              // Use both points for proper positioning
              const points = drawing.properties.coordinates.map(coord => ({
                time: coord.time,
                price: coord.price,
                offset: 0
              }));

              activeChart.createShape(
                points,
                {
                  shape: drawing.shape,
                  overrides: drawing.overrides,
                  text: drawing.text,
                  lock: drawing.lock,
                  disableSelection: false,
                  disableSave: false,
                  disableUndo: false,
                  properties: {
                    stopLevel: Math.round((drawing.properties.stopPrice - points[0].price) / 0.00001),
                    profitLevel: Math.round((drawing.properties.profitPrice - points[0].price) / 0.00001),
                  }
                }
              );
            }
          } catch (err) {
            console.error('Error restoring drawing:', err, drawing);
          }
        });
      }
    });
  }, 1000);
}
                */
              // Create the new button for placing orders
              if (!isDemo) {
                const placeOrderButton = chartWidgetRef.current.createButton();
                placeOrderButton.title = 'Place Order';
                placeOrderButton.className = 'apply-common-tooltip';
                placeOrderButton.style.cursor = 'pointer';
                placeOrderButton.innerHTML = '🛒 Place New Order'; 
                placeOrderButton.addEventListener('click', () => {
                  console.log('Place Order button clicked');

                  if (chartWidgetRef.current) {
                    // Get values from the drawing tools
                    const { entryValue, stopLossValue, takeProfitValue, drawingId } = getDrawingToolValues(lastClickedDrawingId);
                    console.log('Drawing Tool Values:', { entryValue, stopLossValue, takeProfitValue, drawingId });
                
                    // Add a delay before opening the modal
                    setTimeout(() => {
                      // Pass these values to the order modal
                      handleIsOrderModalOpen(true, {
                        entryValue,
                        stopLossValue,
                        takeProfitValue
                      }, drawingId);
                    }, 100); // Adjust the delay as necessary (100 milliseconds in this example)
                  } else {
                    console.error('Chart widget is not initialized.');
                  }
                });
                console.log('Place Order button created and added:', placeOrderButton);
              }
            }).catch((error) => {
              console.error('Error waiting for header to be ready:', error);
            });

            // Capture click event on drawing tools
            chartWidgetRef.current._container.addEventListener('click', (event) => {
              if (isDemo) {
                setSnackbarMessage("Drawing tools not available in demo mode");
                setSnackbarColor('warning');
                setShowSnackbar(true);
                return;
              }

              console.log('Chart container clicked:', event);
              const drawingValues = getDrawingToolValues();
              if (drawingValues.drawingId) {
                console.log('Drawing Tool Values:', drawingValues);
                handleDrawingToolClick(drawingValues.entryValue, drawingValues.stopLossValue, drawingValues.takeProfitValue, drawingValues.drawingId);
                setLastClickedDrawingId(drawingValues.drawingId);
              }
            });
          });

          // Handle interval changes with demo restrictions
          chartWidgetRef.current.subscribe('onIntervalChanged', async (newInterval) => {
            console.log('Chart interval changed to:', newInterval);
            
            // Update current resolution
            setCurrentResolution(newInterval);
            
            if (isDemo) {
              // If in demo mode, display restriction alert but don't change interval
              setShowFeatureAlert(true);
              return;
            }
          
            try {
              setTradeSnackbarMessage("Loading Data...");
              setTradeSnackbarColor('info');
              setTradeSnackbarOpen(true);
              console.log('Showing snackbar: Loading Data...')


              // Clear existing interval if playing
              if (isPlaying) {
                clearInterval(intervalRef.current);
                setIsPlaying(false);
              }
          
              // Update the datafeed's interval
              if (chartWidgetRef.current && chartWidgetRef.current._options.datafeed) {
                const datafeed = chartWidgetRef.current._options.datafeed;
                
                // Store current playback time before switching intervals
                datafeed.storePlaybackTime();
                
                // Update interval in the datafeed
                datafeed.interval = newInterval;
                datafeed.currentInterval = newInterval;
                console.log('Updated datafeed interval to:', newInterval);
                
                // Reset playback data
                datafeed.playbackBarsData = [];
                datafeed.playbackCurrentIndex = 0;
                
                // First, fetch the new market data
                const apiInterval = mapIntervalToAPI(newInterval);
                await fetchAndStoreMarketData(
                  userID, 
                  sessionId, 
                  symbol, 
                  newInterval, 
                  startDateMillis, 
                  endDateMillis
                );
                
                // Then load and prepare it for playback
                await datafeed.getBarsForNewInterval();
                
                console.log('Successfully fetched new interval data');
              }
            } catch (error) {
              console.error('Error handling interval change:', error);
              setTradeSnackbarMessage("Error loading data");
              setTradeSnackbarColor('error');
              setTradeSnackbarOpen(true);
            } finally {
              // Hide snackbar
              setTradeSnackbarOpen(false);
              console.log('Hiding snackbar')


            }
          });
        }
      }, 500);
    };

    renderChartWithDelay();

    // Cleanup function to avoid memory leaks
    return () => {
      console.log('Cleaning up chart widget...');
      if (chartContainerRef.current) {
        chartContainerRef.current.innerHTML = '';
      }
      if (chartWidgetRef.current) {
        chartWidgetRef.current.remove();
        chartWidgetRef.current = null;
      }
    };
  }, [symbol, startDate, endDate, sessionId, userID, apiInterval, startDateMillis, endDateMillis, interval, selectedInterval, chartTheme, handleDrawingToolClick, isDemo]);
  
// Reusable guard clause for chart widget
const checkChartWidget = () => {
  if (!chartWidgetRef.current) {
    console.warn('Chart widget not initialized');
    return false;
  }
  
  if (!chartWidgetRef.current._options.datafeed) {
    console.warn('Datafeed not initialized');
    return false;
  }

  return true;
};
  function getDrawingToolValues() {
    if (!chartWidgetRef.current) {
        console.error('Chart widget is not initialized.');
        return {};
    }

    const chart = chartWidgetRef.current.chart();
    const drawings = chart.getAllShapes();
    console.log('All Drawings in getDrawingToolValues:', drawings);

    let entryValue, stopLossValue, takeProfitValue, drawingId;
    const pipMultiplier = 0.00001; // For pairs like EUR/USD; adjust as needed for other pairs

    drawings.forEach(drawing => {
        console.log('Detailed Drawing Data:', JSON.stringify(drawing, null, 2));

        if (drawing.name === 'long_position' || drawing.name === 'short_position') {
            const shape = chart.getShapeById(drawing.id);
            console.log('Shape properties:', shape);

            if (shape && shape.getPoints) {
                const points = shape.getPoints();
                console.log('Points data:', points);
                entryValue = points[0]?.price;
                drawingId = drawing.id; // Set the drawing ID
            }

            // Access pip values for stop loss and take profit
            const stopLevelPips = parseFloat(shape?._source?._properties?.stopLevel?._value) || NaN;
            const profitLevelPips = parseFloat(shape?._source?._properties?.profitLevel?._value) || NaN;

            // Convert pip values to price offsets and calculate SL and TP
            if (!isNaN(stopLevelPips) && entryValue) {
                const stopOffset = stopLevelPips * pipMultiplier;
                stopLossValue = drawing.name === 'long_position' 
                    ? entryValue - stopOffset
                    : entryValue + stopOffset;
            }
            if (!isNaN(profitLevelPips) && entryValue) {
                const profitOffset = profitLevelPips * pipMultiplier;
                takeProfitValue = drawing.name === 'long_position' 
                    ? entryValue + profitOffset
                    : entryValue - profitOffset;
            }

            console.log('Extracted Entry Value:', entryValue);
            console.log('Calculated Stop Loss Value:', stopLossValue);
            console.log('Calculated Take Profit Value:', takeProfitValue);
        }
    });

    return { entryValue, stopLossValue, takeProfitValue, drawingId };
}
const saveChartLayout = async (layout) => {
  try {
    if (!layout) {
      console.error('No layout data provided');
      return;
    }

    console.log('Raw layout data:', layout);

    // Save layout directly without additional processing
    const sessionRef = doc(db, 'Users', userID, 'backtesting', currentSessionId);
    await updateDoc(sessionRef, {
      chartLayout: layout
    });

    console.log('Layout saved successfully to database');
  } catch (error) {
    console.error('Error saving layout:', error);
  }
};

const loadLayout = async () => {
  try {
    const sessionRef = doc(db, 'Users', userID, 'backtesting', currentSessionId);
    const sessionSnapshot = await getDoc(sessionRef);
    
    if (sessionSnapshot.exists()) {
      const data = sessionSnapshot.data();
      if (data.chartLayout) {
        console.log('Loading layout:', data.chartLayout);
        return data.chartLayout;
      }
    }
    console.log('No layout found');
    return null;
  } catch (error) {
    console.error('Error loading layout:', error);
    return null;
  }
};

const handlePlayPause = async () => {
  if (!checkChartWidget()) return;
  console.log('handlePlayPause called');

  if (isPlaying) {
    console.log('Pausing playback');
    clearInterval(intervalRef.current);
    setIsPlaying(false);
  } else {
    console.log('Starting playback');
    if (chartWidgetRef.current && chartWidgetRef.current._options.datafeed) {
      const datafeed = chartWidgetRef.current._options.datafeed;
      const currentResolution = chartWidgetRef.current.chart().resolution();
      datafeed.interval = currentResolution;
      datafeed.currentInterval = currentResolution; // Sync interval
      console.log("Updated interval for playback:", currentResolution);

      await datafeed.getBarsForPlayback();

      intervalRef.current = setInterval(async () => {
        const nextCandle = await datafeed.getNextCandle();
        if (nextCandle) {
          console.log("Next Candle:", nextCandle);

          const updatedTrades = trades.map(trade => {
            if (trade.status === "Open" && trade.trailingStopLoss) {
              applyTrailingStopLoss(trade, nextCandle.close, nextCandle.time, contractSize);
            }
            checkTakeProfitStopLoss(
              nextCandle.close, trade, nextCandle.high, nextCandle.low, userID, sessionId, currentSessionId, nextCandle.time, contractSize,
              setTradeSnackbarMessage, setTradeSnackbarColor, setTradeSnackbarOpen
            );
            checkLimitOrderHit(
              nextCandle.close, trade, nextCandle.high, nextCandle.low, userID, sessionId, currentSessionId, nextCandle.time, contractSize,
              setTradeSnackbarMessage, setTradeSnackbarColor, setTradeSnackbarOpen
            );
            if (trade.status === "Open") {
              const unrealizedProfit = calculateUnrealizedProfit(nextCandle.close, trade, contractSize);
              console.log(`Unrealized Profit for Trade ${trade.tradeNumber}:`, unrealizedProfit);
              return { ...trade, unrealizedProfit };
            }
            return trade;
          });

          updateTrades(updatedTrades);
          console.log('Updated Trades:', updatedTrades);
        } else {
          console.log('No more candles to fetch');
          clearInterval(intervalRef.current);
          setIsPlaying(false);
          setShowEndSessionSnackbar(true);
        }
      }, 500 / playbackSpeed);

      setIsPlaying(true);
    } else {
      console.error("Chart widget or datafeed is not initialized.");
    }
  }
};


const handleManualUpdate = async () => {
  if (chartWidgetRef.current && chartWidgetRef.current._options.datafeed) {
    console.log('Manually updating bars for new interval');
    const newResolution = chartWidgetRef.current.chart().resolution();
    chartWidgetRef.current._options.datafeed.interval = newResolution;
    console.log("Updated interval for manual update:", newResolution);
    await chartWidgetRef.current._options.datafeed.getBarsForNewInterval();
  } else {
    console.error("Chart widget or datafeed is not initialized.");
  }
};

// Set up the automatic trigger when the component mounts
useEffect(() => {
  const intervalTimer = setInterval(() => {
    if (chartWidgetRef.current) {
      const newResolution = chartWidgetRef.current.chart().resolution();
      
      // Only fetch if the resolution has changed
      if (newResolution !== currentResolution) {
        console.log('Resolution changed, fetching new bars');
        setCurrentResolution(newResolution); // Update the state with the new resolution
        handleManualUpdate();
      }
    }
  }, 1200); // Check every 1 seconds

  // Clean up the interval when the component unmounts
  return () => clearInterval(intervalTimer);
}, [currentResolution]); // Add currentResolution to dependencies

// Update the playback speed instantly without pausing
const handlePlaybackSpeedChange = (e) => {
  if (!checkChartWidget()) return;
  const newSpeed = Number(e.target.value);
  setPlaybackSpeed(newSpeed);

  if (isPlaying) {
    console.log('Updating playback speed');
    clearInterval(intervalRef.current); // Clear the current interval
    intervalRef.current = setInterval(async () => {
      const nextCandle = await chartWidgetRef.current._options.datafeed.getNextCandle();
      if (nextCandle) {
        console.log("Next Candle:", nextCandle);
        // The chart will update via the subscription automatically
      } else {
        console.log('No more candles to fetch');
        clearInterval(intervalRef.current);
        setIsPlaying(false);
      }
    }, 700 / newSpeed); // Adjust interval with new speed dynamically
  }
};

const handleResetSpeed = () => {
  if (!checkChartWidget()) return;
  console.log('handleResetSpeed called');
  setPlaybackSpeed(1); // Reset playback speed to 1x

  if (isPlaying) {
    clearInterval(intervalRef.current); // Clear the current interval
    intervalRef.current = setInterval(async () => {
      const nextCandle = await chartWidgetRef.current._options.datafeed.getNextCandle();
      if (nextCandle) {
        console.log("Next Candle:", nextCandle);
      } else {
        console.log('No more candles to fetch');
        clearInterval(intervalRef.current);
        setIsPlaying(false);
      }
    }, 700 / 1); // Restart the interval with reset speed
  }
};

const handleStepForward = async () => {
  if (!checkChartWidget()) return;
  console.log('handleStepForward called');
  clearInterval(intervalRef.current);

  if (chartWidgetRef.current && chartWidgetRef.current._options.datafeed) {
    const datafeed = chartWidgetRef.current._options.datafeed;
    const currentResolution = chartWidgetRef.current.chart().resolution();

    // Check if the interval has changed and if datafeed is initialized
    if (!datafeed.isInitialized || datafeed.interval !== currentResolution) {
      datafeed.interval = currentResolution;
      console.log("Updated interval for step forward:", currentResolution);

      // RESET THE CHART: This is critical to prevent time violations
      chartWidgetRef.current.activeChart().resetData();
      
      // Wait a moment for the reset to complete
      await new Promise(resolve => setTimeout(resolve, 300));
      
      await chartWidgetRef.current._options.datafeed.getBarsForNewInterval();
      await chartWidgetRef.current._options.datafeed.getBarsForPlayback();
      datafeed.isInitialized = true;
    }

    const nextCandle = await datafeed.getNextCandle();
    if (nextCandle) {
      console.log("Step Forward Candle:", nextCandle);
      
      const updatedTrades = trades.map(trade => {
        if (trade.status === "Open" && trade.trailingStopLoss) {
          applyTrailingStopLoss(trade, nextCandle.close, nextCandle.time, contractSize);
        }
        checkTakeProfitStopLoss(
          nextCandle.close, trade, nextCandle.high, nextCandle.low, userID, sessionId, currentSessionId, nextCandle.time, contractSize,
          setTradeSnackbarMessage, setTradeSnackbarColor, setTradeSnackbarOpen
        );
        checkLimitOrderHit(
          nextCandle.close, trade, nextCandle.high, nextCandle.low, userID, sessionId, currentSessionId, nextCandle.time, contractSize,
          setTradeSnackbarMessage, setTradeSnackbarColor, setTradeSnackbarOpen
        );
        if (trade.status === "Open") {
          const unrealizedProfit = calculateUnrealizedProfit(nextCandle.close, trade, contractSize);
          console.log(`Unrealized Profit for Trade ${trade.tradeNumber}:`, unrealizedProfit);
          return { ...trade, unrealizedProfit };
        }
        return trade;
      });

      updateTrades(updatedTrades);
      console.log('Updated Trades:', updatedTrades);      


      // Notify subscribers and update datafeed state
      chartWidgetRef.current._options.datafeed.notifySubscribers(nextCandle);
      datafeed.currentCandle = nextCandle;
      datafeed.currentInterval = currentResolution;
    } else {
      console.log("No more candles to step forward.");
      clearInterval(intervalRef.current);
      setIsPlaying(false);
    }
  } else {
    console.error("Chart widget or datafeed is not initialized.");
  }
};


return (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative' }}>
        {isDemo && <DemoRestrictionAlert />} {/* Add this */}
        <Snackbar
        open={isIntervalLoading}
        message="Loading Data..."
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ mt: 2 }} // Margin top to avoid overlapping with other UI elements
      />
        {console.log('isIntervalLoading:', isIntervalLoading)} {/* Add this here */}
    {/* Draggable Toolbar Section */}
    <Draggable bounds="parent" handle=".drag-handle">
      <div style={{ position: 'absolute', zIndex: 10, width: '230px', height: '60px', right: `500px` }}>
        <Toolbar
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '5px',
            backgroundColor: theme.palette.background.default || '#bac7db',
            borderRadius: '8px',
            marginBottom: '10px',
            height: '100%'
          }}
        >
          {/* Drag Handle */}
          <div className="drag-handle" style={{ cursor: 'move', padding: '0 5px', display: 'flex', alignItems: 'center' }}>
            <div style={{ display: 'flex', flexDirection: 'column', marginRight: '5px' }}>
              {[...Array(3)].map((_, i) => (
                <div key={i} style={{ display: 'flex', marginBottom: '2px' }}>
                  <div style={{ width: '4px', height: '4px', backgroundColor: iconColor, marginRight: '2px' }}></div>
                  <div style={{ width: '4px', height: '4px', backgroundColor: iconColor }}></div>
                </div>
              ))}
            </div>
          </div>
          {/* Playback Control Buttons */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton sx={{ color: iconColor, padding: '4px', fontSize: '25px' }} onClick={handlePlayPause}>
              {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
            </IconButton>
            <IconButton sx={{ color: iconColor, padding: '4px', fontSize: '25px' }} onClick={handleStepForward}>
              <SkipNextIcon />
            </IconButton>
          </Box>

          {/* Timeframe and Playback Speed Controls */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              label="Speed"
              type="range"
              value={playbackSpeed}
              onChange={handlePlaybackSpeedChange}
              inputProps={{ min: 0.1, max: 10, step: 0.1 }}
              sx={{ width: '80px', color: iconColor }}
              className="custom-range"
            />
            <MDTypography variant="body2" sx={{ fontSize: '12px', color: iconColor, cursor: 'pointer' }} onClick={handleResetSpeed}>{playbackSpeed}x</MDTypography>
          </Box>
        </Toolbar>
      </div>
    </Draggable>
    <Snackbar
      open={showEndSessionSnackbar}
      autoHideDuration={7000}
      onClose={() => setShowEndSessionSnackbar(false)}
      anchorOrigin={{ vertical: 'center', horizontal: 'center' }}
      sx={{
        position: 'absolute',
        top: '20%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 9999
      }}
    >
      <Alert 
        onClose={() => setShowEndSessionSnackbar(false)} 
        severity="info" 
        sx={{ 
          width: '100%',
          backgroundColor: theme.palette.background.default,
          color: theme.palette.text.primary,
          boxShadow: '0px 3px 10px rgba(0, 0, 0, 0.4)',
          '& .MuiAlert-action': {
            color: iconColor // Apply iconColor to close button
          }
        }}
      >
        <MDTypography variant="body2">
          Session completed! You've reached the end of your selected date range.
        </MDTypography>
      </Alert>
    </Snackbar>
    
    {isLoading && <div>Loading chart...</div>} {/* Display loading message */}
    
    <div ref={chartContainerRef} className={'TVChartContainer'} id={`tradingview-chart-${sessionId}`}></div>
  </div>
);
});

export default TradingViewChart;